/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-05 11:29:10
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Tree } from 'react-arborist';
import { Icon, TextField } from '@shopify/polaris';
import {
  SearchMinor,
  EmbedMinor,
  ChevronRightMinor,
  ChevronDownMinor
} from '@shopify/polaris-icons';
import Classnames from 'classnames';
import { withI18n } from '@shopify/react-i18n';

import IconFolder from 'src/assets/images/shopify-integration-settings/folder.svg?react';
import * as ShopifyIntegrationSettingsAction from 'src/stores/actions/shopify-integration/shopify-integration-settings-actions';
import * as TemplateInstanceAction from 'src/stores/actions/shopify-integration/template-instance-actions';
import Emitter, { EVENT_KEY } from 'src/libs/eventEmitter';

type Props = {};

type ComposedProps = Props & WithI18nProps;

type State = {};

class MenuDeveloper extends Component<ComposedProps, State> {
  constructor(props) {
    super(props);
    this.state = {
      search: ''
    };
  }

  componentDidMount() {
    Emitter.on(EVENT_KEY.SELECT_NODE_TREE_CODE_EDITOR, this.handleSelectNodeTree);
  }

  componentWillUnmount() {
    Emitter.off(EVENT_KEY.SELECT_NODE_TREE_CODE_EDITOR, this.handleSelectNodeTree);
  }

  handleSelectMenu = menuItem => {
    this.props.updateFieldReducer('activeMenuItem', menuItem.value);
  };

  handleChangeSearch = value => {
    this.setState({
      search: value
    });
  };

  handleClearSearch = () => {
    this.setState({
      search: ''
    });
  };

  handleSelectNodeTree = widgetName => {
    if (this.tree) {
      if (widgetName) {
        this.tree.select(this.tree.get(widgetName));
      } else {
        this.tree.deselectAll();
      }
    }
  };

  handleSelectSection = (template, name) => {
    const { updateMultiFieldsReducer, templatesEditing, widgetVersion, getWidgetVersions } =
      this.props;
    Emitter.emit(EVENT_KEY.SAVE_EDITOR_STATE);

    const customizeTemplate = !_.isEmpty(template.customizeTemplate)
      ? template.customizeTemplate
      : template.widgetTemplate;

    const payload = {
      customizeTemplate,
      codeEditTemplate: template
    };

    if (_.isEmpty(templatesEditing[template?.widgetId])) {
      payload.templatesEditing = {
        ...templatesEditing,
        [template?.widgetId]: {
          widgetName: template.widgetName,
          customizeTemplate,
          originalTemplate: customizeTemplate,
          isEditing: false,
          name,
          sourceLiquid: template.sourceLiquid
        }
      };
    }

    if (_.isEmpty(_.get(widgetVersion, `${template?.widgetId}.versions`, []))) {
      getWidgetVersions(template?.templateId, template?.widgetId);
    }

    updateMultiFieldsReducer(payload);
  };

  handleActivate = node => {
    const { codeEditTemplate } = this.props;
    if (node.id === codeEditTemplate?.widgetName) {
      return;
    }

    if (!_.isEmpty(node.data.templateData)) {
      const objectWidgets = this.buildObjectDataInstantWidget();
      if (objectWidgets[node.data?.templateData?.widgetName]) {
        this.handleSelectSection(
          objectWidgets[node.data.templateData?.widgetName],
          node.data?.name
        );
      }
    }
  };

  handleToggleTreeNode = id => {
    const { codeEditTemplate } = this.props;
    if (id !== codeEditTemplate?.widgetName && this.tree.isOpen(id)) {
      setTimeout(() => {
        this.tree.select(this.tree.get(codeEditTemplate?.widgetName));
      }, 0);
    }
  };

  getDataFolder = () => {
    const { templateFolder } = this.props;
    const result = [];
    Object.keys(templateFolder).forEach(key => {
      if (key === '_files') {
        const files = templateFolder[key];
        // const contentFile
        Object.keys(files).forEach(keyFile => {
          result.push({
            id: files[keyFile]?.widgetName,
            name: key,
            templateData: files[keyFile]
          });
        });
      } else {
        result.push({
          id: key,
          name: key,
          children: this.buildDataFolder(templateFolder[key])
        });
      }
    });

    return result;
  };

  buildDataFolder = folder => {
    const result = [];
    Object.keys(folder).forEach(key => {
      if (key === '_files') {
        const files = folder[key];
        Object.keys(files).forEach(keyFile => {
          result.push({
            id: files[keyFile]?.widgetName,
            name: keyFile,
            templateData: files[keyFile]
          });
        });
      } else {
        result.push({
          id: key,
          name: key,
          children: this.buildDataFolder(folder[key])
        });
      }
    });
    return result;
  };

  buildObjectDataInstantWidget = () => {
    const { instantWidget } = this.props;
    const flattenWidgets = _.flattenDeep(Object.values(instantWidget.widgets || {}));
    const result = flattenWidgets.reduce((cur, next) => {
      return {
        ...cur,
        [next.widgetName]: next
      };
    }, {});
    return result;
  };

  render() {
    const { i18n } = this.props;
    const { search } = this.state;
    const menuClass = Classnames('visual-editor-menu-developer');
    const objectWidgets = this.buildObjectDataInstantWidget();

    const data = this.getDataFolder();

    return (
      <div className={menuClass}>
        <div className="title-wrapper">
          <h2 className="menu-title">
            {i18n.translate('AdminHTML.ShopifyIntegrationSettings.Developer.title')}
          </h2>
        </div>

        <div className="menu-container">
          <div className="menu-container__search">
            <TextField
              onChange={this.handleChangeSearch}
              value={search}
              prefix={<Icon source={SearchMinor} color="base" />}
              placeholder={i18n.translate('AdminHTML.Common.Action.search')}
              clearButton
              onClearButtonClick={this.handleClearSearch}
            />
          </div>

          <Tree
            initialData={data}
            searchTerm={search}
            searchMatch={(node, search) =>
              node.data.name.toLowerCase().includes(search.toLowerCase())
            }
            ref={e => (this.tree = e)}
            openByDefault={true}
            width={280}
            height={750}
            indent={24}
            rowHeight={28}
            overscanCount={1}
            rowClassName="tree-item"
            onActivate={this.handleActivate}
            onToggle={this.handleToggleTreeNode}
            disableMultiSelection
            disableEdit
            disableDrag
            disableDrop>
            {({ node, style }) => {
              return (
                <div
                  style={{ ...style }}
                  className={Classnames('tree-item-content', {
                    active: node.isSelected && node.isLeaf,
                    edited: !_.isEqual(
                      node.data?.templateData?.widgetTemplate,
                      objectWidgets[node.data?.templateData?.widgetName]?.customizeTemplate
                    )
                  })}
                  onClick={!node.isLeaf ? () => node.toggle() : null}>
                  {node.isLeaf ? <Icon source={EmbedMinor} /> : <IconFolder />}
                  <p className="truncate" style={{ maxWidth: '235px' }} title={node.data.name}>
                    {node.data.name}
                  </p>
                  {!node.isLeaf ? (
                    <span style={{ marginLeft: 'auto' }}>
                      <Icon source={node.isOpen ? ChevronDownMinor : ChevronRightMinor} />
                    </span>
                  ) : null}
                </div>
              );
            }}
          </Tree>
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  updateFieldReducer: (fieldName, data) =>
    dispatch(ShopifyIntegrationSettingsAction.handleUpdateField(fieldName, data)),
  updateMultiFieldsReducer: data =>
    dispatch(ShopifyIntegrationSettingsAction.handleUpdateMultiFields(data)),
  getWidgetVersions: (templateId, widgetId) =>
    dispatch(TemplateInstanceAction.getWidgetVersions(ownProps.i18n, templateId, widgetId))
});

const mapStateToProps = (state, props) => {
  return {
    instantWidget: _.get(state, 'templateInstance.templateInstance', {}),
    codeEditTemplate: _.get(state, 'shopifyIntegrationSettings.codeEditTemplate', {}),
    selectedPage: _.get(state, 'shopifyIntegrationSettings.selectedPage', ''),
    templatesEditing: _.get(state, 'shopifyIntegrationSettings.templatesEditing', {}),
    templateFolder: _.get(state, 'templateInstance.templateInstance.templates', {}),
    widgetVersion: _.get(state, 'templateInstance.widgetVersion', {})
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(MenuDeveloper));
